import {computed, makeObservable, observable} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import MoneyRenderer from "../utils/MoneyRenderer";
import defaultImage from "../../media/background_images/default-image-sapore.jpg";
import errorToast from "../../components/toast/ErrorToast";
import successToast from "../../components/toast/SuccessToast";


export class CartCheckoutFeature {
    constructor(application) {
        this._application = application
        this._initialOrder = {
            Articulos: [],
            FechaProgramada: new Date(),
            Cliente: {
                ArticulosListaPrecio: [{FechaAplicaAumento: new Date()}]
            },
            VerModalMultiplePuntoEntrega: this._application.order === null
        }
        this._customerArticlePriceList = null
        this._orderPrices = undefined
        this._recommended = []
        this._nonWorkingDay = null
        this._applyIncreaseAlert = false
        this._loaded = false
        this._isModalCantEditOrderOpened = false
        this._paymentProofFiles = []
        this._customerTaxProfile = this._noCustomerTaxProfile()

        makeObservable(this, {
            _initialOrder: observable,
            _recommended: observable,
            _nonWorkingDay: observable,
            _applyIncreaseAlert: observable,
            _loaded: observable,
            loaded: computed,
            _orderPrices: observable,
            orderPrices: computed,
            _isModalCantEditOrderOpened: observable,
            isModalCantEditOrderOpened: computed,
            _paymentProofFiles: observable,
            paymentProofFiles: computed,
            _customerTaxProfile: observable,
            advancePaymentProofNeeded: computed,
            canConfirmOrder: computed,
        });
    }

    async load() {
        if (this._application.hasOrder()) {
            await this._getOrderPrices()
            await this._getCustomerTaxProfile()
            if (this.advancePaymentProofNeeded) {
                await this._loadAdvancePaymentProof()
            }
        }
        this.finishLoading()
    }

    get loaded() {
        return this._loaded;
    }

    set loaded(value) {
        this._loaded = value;
    }

    get orderPrices() {
        return this._orderPrices;
    }

    set orderPrices(value) {
        this._orderPrices = value;
    }

    finishLoading() {
        this._loaded = true;
    }

    get initialOrder() {
        return this._initialOrder;
    }

    set initialOrder(value) {
        this._initialOrder = value;
    }

    get recommended() {
        return this._recommended;
    }

    set recommended(value) {
        this._recommended = value;
    }

    get nonWorkingDay() {
        return this._nonWorkingDay;
    }

    set nonWorkingDay(value) {
        this._nonWorkingDay = value;
    }

    get applyIncreaseAlert() {
        return this._applyIncreaseAlert;
    }

    set applyIncreaseAlert(value) {
        this._applyIncreaseAlert = value;
    }

    get isModalCantEditOrderOpened() {
        return this._isModalCantEditOrderOpened
    }

    set isModalCantEditOrderOpened(value) {
        this._isModalCantEditOrderOpened = value
    }

    _noCustomerTaxProfile() {
        return {advancePaymentRequired: false};
    }

    get advancePaymentProofNeeded() {
        return this._customerTaxProfile.advancePaymentRequired;
    }

    get paymentProofFiles() {
        return this._paymentProofFiles;
    }

    set paymentProofFiles(value) {
        this._paymentProofFiles = value;
    }

    get canConfirmOrder() {
        if (this.advancePaymentProofNeeded) {
            return this._paymentProofFiles.length > 0;
        }
        return true;
    }

    order() {
        return this._application.getOrder()
    }

    removeOrder() {
        this._application.removeOrder()
    }

    productImage(article) {
        const url = article.file;
        if (url.includes('invalid_url')){
            return defaultImage;
        }
        return url;
    }

    async confirmOrder(handleSuccess) {
        if (!this._application.hasOrder()) {
            errorToast("El pedido ya no está disponible. Actualizá y volvé a intentar.")
            handleSuccess()
            return;
        }
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._application.removeOrder()
                handleSuccess()
            },
            handlesError: async (response) => {
                await this.handleOrderEditionError(response);
            }
        });
        this._application.apiClient().closeOrder(this.order().number, responseHandler);
    }

    async handleOrderEditionError(response) {
        if (response.editionFailed?.()) {
            this._isModalCantEditOrderOpened = true
        } else if (response.orderWasDeleted?.()) {
            await this._application.session().logOutAndRemoveOrder()
            return errorToast("El pedido fue eliminado. Por favor, ingrese nuevamente y acceda a 'Mis Pedidos' para ver sus pedidos activos.")
        } else if (response.noAdvancePaymentProof?.()){
            return errorToast("No se ha cargado un comprobante de pago para el pedido")
        } else {
            return errorToast("No se pudo editar el pedido.");
        }
    }

    _getOrderPrices() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._orderPrices = response.orderPrices();
            },
            handlesError: async (response) => {
                if (response.orderWasDeleted?.()) {
                    await this._application.session().logOutAndRemoveOrder()
                    return errorToast("El pedido fue eliminado. Por favor, ingrese nuevamente y acceda a 'Mis Pedidos' para ver sus pedidos activos.")
                }
            }
        });
        return this._application.apiClient().orderPrices(this.order().number, responseHandler)
    }

    _getCustomerTaxProfile() {
        const currentOrder = this.order();
        const customerBusinessName = currentOrder?.customer?.businessName ?? currentOrder?.customerBusinessName;
        if (!customerBusinessName) {
            this._customerTaxProfile = this._noCustomerTaxProfile();
            return;
        }
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._customerTaxProfile = response.taxProfile();
            },
            handlesError: async (response) => {
                this._customerTaxProfile = this._noCustomerTaxProfile();
                console.error("Error loading customer tax profile:", response);
            }
        });
        return this._application.apiClient().customerTaxProfile(customerBusinessName, responseHandler);
    }

    async _loadAdvancePaymentProof() {
        const orderNumber = this.order().number;

        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                const advancePaymentProof = response.advancePaymentProof();
                if (advancePaymentProof && advancePaymentProof.file && advancePaymentProof.file.name) {
                    const existingFile = this._mockFile(advancePaymentProof.file);
                    this._paymentProofFiles = [existingFile];
                }
            },
            handlesError: async (response) => {
                errorToast("No se pudo obtener el comprobante de pago. Intente nuevamente o comuniquese con el administrador.");
            }
        });
        return this._application.apiClient().getAdvancePaymentProof(orderNumber, responseHandler);
    }

    _mockFile(file) {
        return {
            name: file.name,
            size: 0
        };
    }

    async handlePaymentProofFilesChange(files) {
        this._paymentProofFiles = files;

        const orderNumber = this.order().number;
        const file = files.length > 0 ? files[0] : null;

        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                const proof = response.advancePaymentProof();
                if (proof) {
                    successToast("Comprobante de pago cargado exitosamente");
                } else {
                    successToast("Comprobante de pago eliminado exitosamente");
                }
            },
            handlesError: async (response) => {
                errorToast("No se pudo procesar el comprobante de pago. Intente nuevamente.");
            }
        });
        return this._application.apiClient().registerAdvancePaymentProof(orderNumber, file, responseHandler);
    }

    async getOrder() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._application.setOrder(response.order())
            },
            handlesError: async (response) => {
                if (response.orderWasDeleted?.()) {
                    await this._application.session().logOutAndRemoveOrder()
                    return errorToast("El pedido fue eliminado. Por favor, ingrese nuevamente y acceda a 'Mis Pedidos' para ver sus pedidos activos.")
                }
            }
        });
        return this._application.apiClient().orderDetail(this._application.getOrder().number, responseHandler);
    }

    async addProductToCartAndGetOrderPrices(orderArticle, quantityToAdd){
        await this.addProductToCart(orderArticle, quantityToAdd)
        await this._getOrderPrices()
    }

    async addProductToCart(orderArticle, quantityToAdd) {
        if (!this._application.hasOrder()) {
            return;
        }
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._application.setOrder(response.order())
            },
            handlesError: async (response) => {
                await this.handleOrderEditionError(response);
            }
        });
        const article = {
            code: orderArticle.code,
            amount: quantityToAdd
        }
        return this._application.apiClient().addProductToCart(this.order().number, article, responseHandler);
    }

    calculatePriceAmountAndTotal(articleInOrder) {
        const amount = articleInOrder.amount;
        return {
            'price': this.unitPriceOf(articleInOrder),
            'amount': amount,
            'total': this.totalForArticle(articleInOrder),
            'itemPrice': this.itemPriceFor(articleInOrder)
        }
    }

    editOrdersPurchaseOrderNumber(purchaseOrdersNumbers) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._application.setOrder(response.order())
            },
            handlesError: async (response) => {
                await this.handleOrderEditionError(response);
            }
        });
        this._application.apiClient().editOrdersPurchaseOrderNumber(this.order().number, purchaseOrdersNumbers, responseHandler);
    }

    deleteOrdersArticle(article) {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._application.setOrder(response.order())
            },
            handlesError: (response) => {
                // response.errors().map(error => {
                //     this.showAlert(error);
                // })
                console.log("Error fetching article.")
            }
        });
        this._application.apiClient().deleteOrdersArticle(this.order().number, article.code, responseHandler)
    }

    unitPriceOf(article) {
        const pricedArticle = this._orderPrices.articles.find(pricedArticle => pricedArticle.articleCode.trim() === article.code.trim());
        return pricedArticle ? pricedArticle.unitPrice : 0;
    }

    amountOf(article) {
        const amount = article.amount;
        const unit = amount === 1 ? "Caja" : "Cajas";
        return `${amount} ${unit}`;
    }

    unitsPerAmountOf(article) {
        return article.itemsAmount
    }

    itemUnitAbbreviation(article){
        const unitMapping = {
            UNITS: "un.",
            KG: "kg."
        };
        return unitMapping[article.itemsUnit] || "";
    }

    subtotal() {
        return MoneyRenderer(this._orderPrices.articlesTotalPrice)
    }

    totalTaxes() {
        return MoneyRenderer(this._orderPrices.totalVAT)
    }

    totalGrossIncomeTaxes() {
        return this._orderPrices.totalGrossIncomeTaxes;
    }

    total(order) {
        return MoneyRenderer(this._orderPrices.total)
    }

    totalForArticle(article) {
        const pricedArticle = this._orderPrices.articles.find(pricedArticle => pricedArticle.articleCode.trim() === article.code.trim());
        return pricedArticle ? pricedArticle.totalPrice : 0
    }

    itemPriceFor(article) {
        const pricedArticle = this._orderPrices.articles.find(pricedArticle => pricedArticle.articleCode.trim() === article.code.trim());
        return pricedArticle ? pricedArticle.itemPrice : null
    }

}